import type { RouteRecord } from 'vite-react-ssg';
import { Navigate, Outlet } from 'react-router-dom';
import { RootLayout } from './layouts/RootLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { TierGuard } from './auth/TierGuard';
import { RedirectPreservingSearch } from './components/RedirectPreservingSearch';
import { insightPosts } from './content/insights/registry';

const lazyDefault = (load: () => Promise<{ default: React.ComponentType }>) =>
  async () => ({ Component: (await load()).default });

export const routes: RouteRecord[] = [
  {
    element: <RootLayout />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { index: true, lazy: lazyDefault(() => import('./pages/public/HomePage')) },
          { path: 'platform', lazy: lazyDefault(() => import('./pages/public/PlatformPage')) },
          { path: 'services', lazy: lazyDefault(() => import('./pages/public/ServicesPage')) },
          { path: 'services/managed-it', lazy: lazyDefault(() => import('./pages/public/ManagedItPage')) },
          { path: 'services/platform-builds', lazy: lazyDefault(() => import('./pages/public/PlatformBuildsPage')) },
          { path: 'services/martech', lazy: lazyDefault(() => import('./pages/public/MarTechPage')) },
          { path: 'about', lazy: lazyDefault(() => import('./pages/public/AboutPage')) },
          { path: 'impact', lazy: lazyDefault(() => import('./pages/public/ImpactPage')) },
          { path: 'ai-enablement', lazy: lazyDefault(() => import('./pages/public/AiEnablementPage')) },
          { path: 'insights', lazy: lazyDefault(() => import('./pages/public/InsightsPage')) },
          {
            path: 'insights/:slug',
            lazy: lazyDefault(() => import('./pages/public/InsightDetailPage')),
            // vite-react-ssg uses this to enumerate concrete slugs for SSG.
            getStaticPaths: () => insightPosts.map((p) => `insights/${p.slug}`),
          },
          { path: 'pricing', lazy: lazyDefault(() => import('./pages/public/PricingPage')) },
          { path: 'contact', lazy: lazyDefault(() => import('./pages/public/ContactPage')) },
          { path: 'privacy', lazy: lazyDefault(() => import('./pages/public/PrivacyPage')) },
          { path: 'cookies', lazy: lazyDefault(() => import('./pages/public/CookiePage')) },
          { path: 'terms', lazy: lazyDefault(() => import('./pages/public/TermsPage')) },
          { path: 'login', lazy: lazyDefault(() => import('./pages/public/LoginPage')) },
          // Onboarding redeem handler — public, no MSAL token required (#12216 / #11925).
          // Receives ?token={hex}, POSTs to [AllowAnonymous] /api/v1/onboarding/accept,
          // then navigates to the returned redirectUrl.
          { path: 'onboarding/accept', lazy: lazyDefault(() => import('./pages/public/OnboardingAcceptPage')) },
          // Public proposal / quote page (#12191 — [AllowAnonymous] accept/decline).
          // Token is in the path (not a query param) — route: /proposal/:token.
          { path: 'proposal/:token', lazy: lazyDefault(() => import('./pages/public/PublicProposalPage')) },
          // Story #13242 — the CASE_CSAT_REQUEST email's one-click landing page. The token is a
          // QUERY param (?t=), not a path segment, because the email's three rating links append
          // "&rating=" to a single base URL built server-side (CaseCsatService.BuildSurveyUrl).
          { path: 'case-csat', lazy: lazyDefault(() => import('./pages/public/CaseCsatPage')) },
          // Deprecated post-redemption path. The backend now returns the bare authed
          // /onboarding/welcome (#11925); this redirect only catches sessions that were
          // mid-redeem across the deploy that changed the returned redirectUrl.
          { path: 'portal/onboarding/complete', element: <Navigate to="/onboarding/welcome" replace /> },
          // Public catch-all → 404 page (rendered inside PublicLayout so the
          // visitor keeps header + footer for self-recovery, per spec
          // 404-500-error-pages-2026-05-16.md).
          { path: '*', lazy: lazyDefault(() => import('./pages/public/NotFoundPage')) },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          { path: 'dashboard', lazy: lazyDefault(() => import('./pages/Dashboard')) },
          { path: 'm365-users', lazy: lazyDefault(() => import('./pages/M365Users')) },
          // F5 — read-only Domains & DNS registry view (#13591)
          { path: 'domains', lazy: lazyDefault(() => import('./pages/Domains')) },
          // L1 — client licence position: owned / allocated / spare / suspended (#16313).
          // Outside the TierGuard block below: all three client tiers read this.
          { path: 'licences', lazy: lazyDefault(() => import('./pages/Licences')) },
          // S1 — Shop: the client hardware catalogue (#16316). BROWSE only; baskets and
          // checkout are S2 (#16317). Outside the TierGuard block: browsing is informational,
          // so all three client tiers read it (the basket/checkout gates are ClientAdmin and
          // arrive with S2).
          { path: 'shop', lazy: lazyDefault(() => import('./pages/Shop')) },
          { path: 'security-posture', lazy: lazyDefault(() => import('./pages/SecurityPosture')) },
          { path: 'cases', lazy: lazyDefault(() => import('./pages/MyCases')) },
          { path: 'cases/:id', lazy: lazyDefault(() => import('./pages/CaseDetail')) },
          { path: 'invoices', lazy: lazyDefault(() => import('./pages/MyInvoices')) },
          // #20979 — Work order approvals: the list. Outside the TierGuard blocks: every client tier reads it
          // (BR-WOA-001). /work-orders/:id is the approve screen, registered by #10279.
          { path: 'work-orders', lazy: lazyDefault(() => import('./pages/work-orders/WorkOrders')) },
          // #20977 — Change approvals: the list. Outside the TierGuard blocks: every client tier reads it
          // (BR-CRA-002). /change-approvals/:id is the approve screen, registered by #10270.
          { path: 'change-approvals', lazy: lazyDefault(() => import('./pages/change-approvals/ChangeApprovals')) },
          // #10279 — Work order approval: one work order. Also outside the TierGuard: every tier reads it; only a
          // ClientAdmin sees the decision card, and the API gates approve / reject to ClientAdmin (BR-WOA-001).
          { path: 'work-orders/:id', lazy: lazyDefault(() => import('./pages/work-orders/WorkOrderApproval')) },
          // Onboarding hub — the post-redeem welcome/activation journey (#11925).
          // /onboarding redirects to the canonical /onboarding/welcome so existing
          // links (dashboard banner, bookmarks) keep working.
          // RedirectPreservingSearch, not <Navigate>: survives the account-context canonicaliser (#20888).
          { path: 'onboarding', element: <RedirectPreservingSearch to="/onboarding/welcome" /> },
          { path: 'onboarding/welcome', lazy: lazyDefault(() => import('./pages/OnboardingWelcomePage')) },
          // Authenticated in-portal terms acceptance flow. Renamed from
          // /terms to /accept-terms 2026-05-17 to free /terms for the
          // public Terms of Use page (legal-pages-website-facing-2026-05-16).
          { path: 'accept-terms', lazy: lazyDefault(() => import('./pages/TermsAcceptance')) },
          { path: 'settings', lazy: lazyDefault(() => import('./pages/Settings')) },
          { path: 'requests/new', lazy: lazyDefault(() => import('./pages/requests/SubmitRequest')) },
          { path: 'requests', element: <RedirectPreservingSearch to="/cases" /> },
          // it-admin routes require the ClientAdmin tier (#12629 — CPT-2).
          {
            element: <TierGuard requiredTier="ClientAdmin"><Outlet /></TierGuard>,
            children: [
              // F2.9C — ClientAdmin self-service department management
              { path: 'it-admin/departments', lazy: lazyDefault(() => import('./pages/it-admin/Departments')) },
              // F3.6h — Client Portal Quote view + accept/decline UI (#10292)
              { path: 'it-admin/quotes', lazy: lazyDefault(() => import('./pages/it-admin/quotes/MyQuotes')) },
              { path: 'it-admin/quotes/:id', lazy: lazyDefault(() => import('./pages/it-admin/quotes/QuoteDetail')) },
              // S2 — the cart: ONE named basket, which is a DRAFT quote (#16317). ClientAdmin only
              // and deliberately narrower than /shop above: browsing is informational, but the cart
              // and "Send for pricing" are a commercial act, matching the controller's own
              // ClientAdmin-only authorisation. `?basket={id}` addresses a specific draft (that is
              // how My Quotes opens a draft row); with no parameter it opens the active basket.
              { path: 'cart', lazy: lazyDefault(() => import('./pages/Cart')) },
            ],
          },
          // #20070 — F11.5 self-service (spec §9.2). My requests: ClientAdmin OR ClientUser
          // (ClientAdminAndUser), never ClientBilling. Approvals: ClientAdmin (ClientAdminOnly).
          // Each renders an In-Development page (operator-approved stub) until its screen Story
          // replaces the page file; this route table is not touched again.
          {
            element: <TierGuard anyOfTiers={['ClientAdmin', 'ClientUser']}><Outlet /></TierGuard>,
            children: [
              { path: 'self-service/requests', lazy: lazyDefault(() => import('./pages/self-service/MyHistoryPage')) },
            ],
          },
          {
            element: <TierGuard requiredTier="ClientAdmin"><Outlet /></TierGuard>,
            children: [
              { path: 'self-service/approvals', lazy: lazyDefault(() => import('./pages/self-service/MyApprovalsPage')) },
            ],
          },
          // XCentral Sign — Client Portal signing surface (#12207)
          { path: 'sign', lazy: lazyDefault(() => import('./pages/sign/MySignaturesPage')) },
          { path: 'sign/:envelopeId', lazy: lazyDefault(() => import('./pages/sign/SigningSurfacePage')) },
        ],
      },
    ],
  },
];
